import { useEffect, useRef, useState, memo, useCallback, useMemo } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { TimelineHeader } from './timeline-header'
import { TimelineContent } from './timeline-content'
import { TimelineNavigator } from './timeline-navigator'
import { TrackHeader } from './track-header'
import { TransitionDragTooltip } from './transition-drag-tooltip'
import { TrackRowFrame } from './track-row-frame'
import { useTimelineTracks } from '../hooks/use-timeline-tracks'
import { useItemsStore } from '../stores/items-store'
import { useSelectionStore } from '@/shared/state/selection'
import { useEditorStore } from '@/app/state/editor'
import { useTimelineStore } from '../stores/timeline-store'
import { usePlaybackStore } from '@/shared/state/playback'
import { HOTKEY_OPTIONS } from '@/config/hotkeys'
import { useResolvedHotkeys, useSettingsStore } from '@/features/timeline/deps/settings'
import { useProjectStore } from '@/features/timeline/deps/projects'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu'
import { Plus, Minus, Rows4, Rows3, Rows2, Check, Video, AudioLines } from 'lucide-react'
import { CompositionBreadcrumbs } from './composition-breadcrumbs'
import { useCompositionNavigationStore } from '../stores/composition-navigation-store'
import {
  trackDropIndexRef,
  trackDragOffsetRef,
  trackDragJustDroppedRef,
} from '../hooks/use-track-drag'
import { createClassicTrack, getAdjacentTrackOrder, getTrackKind } from '../utils/classic-tracks'
import { getEmptyTrackIdsForRemoval } from '../utils/track-removal'
import { createLogger } from '@/shared/logging/logger'
import { EDITOR_LAYOUT_CSS_VALUES, getEditorLayout } from '@/app/editor-layout'
import { useTrackHeightResize } from '../hooks/use-track-height-resize'
import { resizeTracksOfKindByDelta } from '../utils/track-resize'
import { useTimelineSettingsStore } from '../stores/timeline-settings-store'
import { resizeAllTracks } from '../stores/actions/track-actions'
import { useZoomStore } from '../stores/zoom-store'
import {
  computeWheelZoomStep,
  COMPACT_TRACK_HEIGHT,
  MAX_TRACK_HEIGHT,
  DEFAULT_TRACK_HEIGHT,
} from '../constants'
import { clearMediaDragData } from '@/features/timeline/deps/media-library-resolver'
import { useNewTrackZonePreviewStore } from '../stores/new-track-zone-preview-store'
import { useTrackDropPreviewStore } from '../stores/track-drop-preview-store'
import { clearAllTimelineDropPreviewOwners } from '../utils/drop-preview-owner'
import {
  isDragEventOverTimelineDropTarget,
  isExternalTimelineDragEvent,
} from '../utils/timeline-external-drag'

const logger = createLogger('Timeline')

/**
 * Track height presets exposed through the track-size flyout. `medium` maps to
 * the default track height so it doubles as a reset.
 */
const TRACK_SIZE_OPTIONS = [
  { id: 'compact' as const, height: COMPACT_TRACK_HEIGHT, icon: Rows4, label: '紧凑' },
  { id: 'medium' as const, height: DEFAULT_TRACK_HEIGHT, icon: Rows3, label: '中等' },
  { id: 'large' as const, height: MAX_TRACK_HEIGHT, icon: Rows2, label: '大' },
] as const

interface TimelineProps {
  duration: number // Total timeline duration in seconds
}

/**
 * Complete Timeline Component
 *
 * Combines:
 * - TimelineHeader (controls, zoom, snap)
 * - Track Headers Sidebar (track labels and controls)
 * - TimelineContent (markers, playhead, tracks, items)
 *
 * Follows modular architecture with granular Zustand selectors
 */
export const Timeline = memo(function Timeline({ duration }: TimelineProps) {
  const hotkeys = useResolvedHotkeys()
  const editorDensity = useSettingsStore((s) => s.editorDensity)
  const editorLayout = getEditorLayout(editorDensity)
  const {
    tracks,
    addTrack,
    removeTracks,
    toggleTrackDisabled,
    toggleTrackLock,
    toggleTrackSyncLock,
    toggleTrackSolo,
  } = useTimelineTracks()
  // Selection state - use granular selectors
  const activeTrackId = useSelectionStore((s) => s.activeTrackId)
  const selectedTrackIds = useSelectionStore((s) => s.selectedTrackIds)
  const setActiveTrack = useSelectionStore((s) => s.setActiveTrack)
  const toggleTrackSelection = useSelectionStore((s) => s.toggleTrackSelection)
  const selectTracks = useSelectionStore((s) => s.selectTracks)
  const selectedTrackIdsSet = useMemo(() => new Set(selectedTrackIds), [selectedTrackIds])

  const visibleTracks = tracks
  const canDeleteEmptyTracks = useItemsStore(
    useCallback(
      (s) => {
        let emptyTrackCount = 0

        for (const track of tracks) {
          if ((s.itemsByTrackId[track.id]?.length ?? 0) === 0) {
            emptyTrackCount += 1
          }
        }

        if (emptyTrackCount === 0) return false
        if (emptyTrackCount < tracks.length) return true
        return tracks.length > 1
      },
      [tracks],
    ),
  )
  const videoTracks = useMemo(
    () => visibleTracks.filter((track) => getTrackKind(track) === 'video'),
    [visibleTracks],
  )
  const audioTracks = useMemo(
    () => visibleTracks.filter((track) => getTrackKind(track) === 'audio'),
    [visibleTracks],
  )

  // Refs for syncing scroll between track headers and timeline content
  const trackHeadersViewportRef = useRef<HTMLDivElement>(null)
  const trackHeadersRootRef = useRef<HTMLDivElement>(null)
  const timelineContentRef = useRef<HTMLDivElement>(null)
  // Unified single-track-list timeline: one scroll container for headers, one
  // for clip rows. Both share scrollTop via an effect below.
  const trackHeadersScrollRef = useRef<HTMLDivElement>(null)
  const trackContentScrollRef = useRef<HTMLDivElement>(null)

  // Store zoom handlers from TimelineContent
  const [zoomHandlers, setZoomHandlers] = useState<{
    handleZoomChange: (newZoom: number) => void
    handleZoomIn: () => void
    handleZoomOut: () => void
    handleZoomToFit: () => void
  } | null>(null)
  const [timelineMetrics, setTimelineMetrics] = useState({
    actualDuration: Math.max(duration, 10),
    timelineWidth: 0,
  })
  const [tracksViewportHeight, setTracksViewportHeight] = useState(0)

  const colorScopesOpen = useEditorStore((s) => s.colorScopesOpen)
  const toggleColorScopesOpen = useEditorStore((s) => s.toggleColorScopesOpen)
  const toggleKeyframeEditorOpen = useEditorStore((s) => s.toggleKeyframeEditorOpen)
  const trackSizePreset = useEditorStore((s) => s.trackSizePreset)
  const setTrackSizePreset = useEditorStore((s) => s.setTrackSizePreset)
  const setTimelineTracks = useTimelineStore((s) => s.setTracks)
  const currentProjectId = useProjectStore((s) => s.currentProject?.id ?? null)
  const isTimelineLoading = useTimelineSettingsStore((s) => s.isTimelineLoading)
  const loadingProjectId = useTimelineSettingsStore((s) => s.loadingProjectId)
  const loadedProjectId = useTimelineSettingsStore((s) => s.loadedProjectId)
  const isTimelineHydratingCurrentProject =
    !!currentProjectId &&
    (loadingProjectId === currentProjectId || isTimelineLoading || loadedProjectId !== currentProjectId)

  useEffect(() => {
    const clearExternalDropPreviews = () => {
      clearAllTimelineDropPreviewOwners()
      useTrackDropPreviewStore.getState().clearGhostPreviews()
      useNewTrackZonePreviewStore.getState().clearGhostPreviews()
    }

    const handleDragEnd = () => {
      clearExternalDropPreviews()
      clearMediaDragData()
    }
    const handleDragOver = (event: DragEvent) => {
      if (!isExternalTimelineDragEvent(event) || isDragEventOverTimelineDropTarget(event)) {
        return
      }

      clearExternalDropPreviews()
    }
    const handleDrop = () => {
      window.setTimeout(() => {
        clearExternalDropPreviews()
        clearMediaDragData()
      }, 0)
    }
    const handleBlur = () => {
      clearExternalDropPreviews()
      clearMediaDragData()
    }

    document.addEventListener('dragenter', handleDragOver, true)
    document.addEventListener('dragover', handleDragOver, true)
    window.addEventListener('dragend', handleDragEnd, true)
    document.addEventListener('drop', handleDrop, true)
    window.addEventListener('blur', handleBlur)
    return () => {
      document.removeEventListener('dragenter', handleDragOver, true)
      document.removeEventListener('dragover', handleDragOver, true)
      window.removeEventListener('dragend', handleDragEnd, true)
      document.removeEventListener('drop', handleDrop, true)
      window.removeEventListener('blur', handleBlur)
    }
  }, [])

  // Keyboard shortcut: Ctrl/Cmd+Shift+A to toggle keyframe editor
  useHotkeys(
    hotkeys.TOGGLE_KEYFRAME_EDITOR,
    (event) => {
      event.preventDefault()
      toggleKeyframeEditorOpen()
    },
    HOTKEY_OPTIONS,
    [toggleKeyframeEditorOpen],
  )

  // State for drop indicator (updated via RAF from drag hook)
  const [dropIndicatorIndex, setDropIndicatorIndex] = useState(-1)

  // Granular selector: only re-render when track dragging state actually changes
  const isTrackDragging = useSelectionStore(
    (s) =>
      (s.dragState?.isDragging &&
        s.dragState.draggedTrackIds &&
        s.dragState.draggedTrackIds.length > 0) ??
      false,
  )

  const { handleTrackResizeStart, handleTrackResizeReset } = useTrackHeightResize()
  const tracksContentHeight = useMemo(
    () => visibleTracks.reduce((sum, track) => sum + track.height, 0),
    [visibleTracks],
  )
  // Top zone (above video) and bottom zone (below audio) act as drop targets
  // for creating new V or A tracks; their heights track the unified viewport.
  const TOP_ZONE_HEIGHT = 24
  const bottomZoneHeight = useMemo(
    () => Math.max(24, tracksViewportHeight - tracksContentHeight - TOP_ZONE_HEIGHT),
    [tracksContentHeight, tracksViewportHeight],
  )
  const getTrackStackOffset = useCallback(
    (sectionTracks: typeof visibleTracks, dropIndex: number, leadingOffset = 0) => {
      return (
        leadingOffset +
        sectionTracks
          .slice(0, Math.max(0, Math.min(dropIndex, sectionTracks.length)))
          .reduce((sum, track) => sum + track.height, 0)
      )
    },
    [],
  )

  useEffect(() => {
    const element = trackHeadersViewportRef.current
    if (!element) return

    const updateHeight = () => {
      setTracksViewportHeight(element.clientHeight)
    }

    updateHeight()
    const observer = new ResizeObserver(updateHeight)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  // Wheel handling in track headers:
  //   Alt+scroll  = resize track heights of the kind under the cursor
  //   Shift+scroll = vertical scroll of the unified track viewport
  //   Ctrl/Cmd+scroll = zoom timeline in/out
  const zoomHandlersRef = useRef(zoomHandlers)
  useEffect(() => {
    zoomHandlersRef.current = zoomHandlers
  }, [zoomHandlers])
  useEffect(() => {
    const el = trackHeadersViewportRef.current
    if (!el) return

    const handler = (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault()
        const z = zoomHandlersRef.current
        if (!z) return
        z.handleZoomChange(computeWheelZoomStep(useZoomStore.getState().level, event.deltaY))
        return
      }

      if (event.altKey) {
        event.preventDefault()
        // Use the hovered header row's kind to decide which lane to resize.
        const trackEl =
          event.target instanceof Element
            ? (event.target.closest('[data-track-id]') as HTMLElement | null)
            : null
        const trackId = trackEl?.dataset.trackId ?? null
        if (!trackId) return
        const currentTracks = useItemsStore.getState().tracks
        const target = currentTracks.find((t) => t.id === trackId)
        const kind = target ? getTrackKind(target) : null
        if (kind !== 'video' && kind !== 'audio') return
        const delta = event.deltaY > 0 ? -4 : 4
        const nextTracks = resizeTracksOfKindByDelta(currentTracks, kind, delta)
        if (nextTracks !== currentTracks) {
          useItemsStore.getState().setTracks(nextTracks)
          useTimelineSettingsStore.getState().markDirty()
        }
        return
      }

      if (event.shiftKey) {
        event.preventDefault()
        const contentScroll = trackContentScrollRef.current
        if (!contentScroll) return
        contentScroll.scrollTop += event.deltaY || event.deltaX
      }
    }

    el.addEventListener('wheel', handler, { passive: false })
    return () => el.removeEventListener('wheel', handler)
  }, [])

  // Set first track as active on mount
  // Use primitive dependencies to avoid re-running on unrelated track changes
  const tracksLength = tracks.length
  const firstTrackId = tracks[0]?.id
  useEffect(() => {
    if (tracksLength > 0 && !activeTrackId && firstTrackId) {
      setActiveTrack(firstTrackId)
    }
  }, [tracksLength, activeTrackId, firstTrackId, setActiveTrack])

  useEffect(() => {
    const source = trackContentScrollRef.current
    const target = trackHeadersScrollRef.current
    if (!source || !target) return

    const handleScroll = () => {
      if (target.scrollTop !== source.scrollTop) {
        target.scrollTop = source.scrollTop
      }
    }

    handleScroll()
    source.addEventListener('scroll', handleScroll, { passive: true })
    return () => source.removeEventListener('scroll', handleScroll)
  }, [tracksLength])

  // Update drop indicator from shared ref (only during drag)
  // Only runs RAF loop when track dragging is active to avoid unnecessary renders
  useEffect(() => {
    if (!isTrackDragging) {
      setDropIndicatorIndex(-1)
      return
    }

    let rafId: number
    const updateDropIndicator = () => {
      const newIndex = trackDropIndexRef.current
      setDropIndicatorIndex((prev) => (prev !== newIndex ? newIndex : prev))
      rafId = requestAnimationFrame(updateDropIndicator)
    }

    rafId = requestAnimationFrame(updateDropIndicator)
    return () => {
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [isTrackDragging])

  // Drag visuals: move all dragged track headers together via direct DOM manipulation.
  // This handles groups (header + children move as one) and multi-select drag.
  useEffect(() => {
    if (!isTrackDragging) return

    const dragState = useSelectionStore.getState().dragState
    if (!dragState?.draggedTrackIds?.length) return

    const draggedIds = new Set(dragState.draggedTrackIds)
    const container = trackHeadersRootRef.current
    if (!container) return

    let rafId: number
    const updateDragVisuals = () => {
      const offset = trackDragOffsetRef.current
      const elements = container.querySelectorAll<HTMLElement>('[data-track-id]')
      for (const el of elements) {
        const trackId = el.getAttribute('data-track-id')
        if (trackId && draggedIds.has(trackId)) {
          el.style.transform = `translateY(${offset}px) scale(1.02)`
          el.style.zIndex = '100'
          el.style.opacity = '0.5'
          el.style.transition = 'none'
          el.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.1)'
        }
      }
      rafId = requestAnimationFrame(updateDragVisuals)
    }

    rafId = requestAnimationFrame(updateDragVisuals)
    return () => {
      cancelAnimationFrame(rafId)
      // Reset styles on all track headers
      if (container) {
        const elements = container.querySelectorAll<HTMLElement>('[data-track-id]')
        for (const el of elements) {
          el.style.transform = ''
          el.style.zIndex = ''
          el.style.opacity = ''
          el.style.transition = ''
          el.style.boxShadow = ''
        }
      }
    }
  }, [isTrackDragging])

  // Keyboard shortcuts for in/out markers
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Another focused panel (e.g. Source Monitor) already handled this key.
      if (e.defaultPrevented) return

      // Ignore if typing in an input, textarea, or contenteditable
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return
      }

      const key = e.key.toLowerCase()

      // Escape - exit composition if inside one
      if (key === 'escape' && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
        const navStore = useCompositionNavigationStore.getState()
        if (navStore.activeCompositionId !== null) {
          e.preventDefault()
          navStore.exitComposition()
          return
        }
      }

      // 'I' key - Set in-point at main playhead
      if (key === 'i' && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
        e.preventDefault()
        const { currentFrame } = usePlaybackStore.getState()
        useTimelineStore.getState().setInPoint(currentFrame)
      }

      // 'Shift+I' key - Set in-point at skimmer playhead when available
      else if (key === 'i' && !e.metaKey && !e.ctrlKey && e.shiftKey && !e.altKey) {
        e.preventDefault()
        const { previewFrame, currentFrame } = usePlaybackStore.getState()
        useTimelineStore.getState().setInPoint(previewFrame ?? currentFrame)
      }

      // 'O' key - Set out-point at main playhead
      if (key === 'o' && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
        e.preventDefault()
        const { currentFrame } = usePlaybackStore.getState()
        useTimelineStore.getState().setOutPoint(currentFrame)
      }

      // 'Shift+O' key - Set out-point at skimmer playhead when available
      else if (key === 'o' && !e.metaKey && !e.ctrlKey && e.shiftKey && !e.altKey) {
        e.preventDefault()
        const { previewFrame, currentFrame } = usePlaybackStore.getState()
        useTimelineStore.getState().setOutPoint(previewFrame ?? currentFrame)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const syncTrackSelectionAfterRemoval = useCallback(
    (removedTrackIds: string[], fallbackTrackId: string | null) => {
      const removedTrackIdsSet = new Set(removedTrackIds)
      const selectionState = useSelectionStore.getState()
      const remainingSelectedTrackIds = selectionState.selectedTrackIds.filter(
        (trackId) => !removedTrackIdsSet.has(trackId),
      )

      if (remainingSelectedTrackIds.length > 0) {
        selectionState.selectTracks(remainingSelectedTrackIds)
        return
      }

      if (selectionState.activeTrackId && !removedTrackIdsSet.has(selectionState.activeTrackId)) {
        return
      }

      selectionState.setActiveTrack(fallbackTrackId)
    },
    [],
  )

  const addVideoTrackToTop = useCallback(() => {
    const newTrack = createClassicTrack({
      tracks,
      kind: 'video',
      order: 0,
      height: editorLayout.timelineTrackHeight,
    })

    addTrack(newTrack)

    setTimeout(() => {
      setActiveTrack(newTrack.id)
    }, 0)
  }, [addTrack, editorLayout.timelineTrackHeight, setActiveTrack, tracks])

  const appendAudioTrackToSection = useCallback(() => {
    const audioAnchorTrack =
      audioTracks[audioTracks.length - 1] ??
      videoTracks[videoTracks.length - 1] ??
      tracks[tracks.length - 1] ??
      null

    const newTrack = createClassicTrack({
      tracks,
      kind: 'audio',
      order: audioAnchorTrack ? getAdjacentTrackOrder(tracks, audioAnchorTrack, 'below') : 0,
      height: editorLayout.timelineTrackHeight,
    })

    setTimelineTracks([...tracks, newTrack])

    setTimeout(() => {
      setActiveTrack(newTrack.id)
    }, 0)
  }, [
    audioTracks,
    editorLayout.timelineTrackHeight,
    setActiveTrack,
    setTimelineTracks,
    tracks,
    videoTracks,
  ])

  // Trigger reflects the persisted preset (saved as a local editor setting).
  const ActiveTrackSizeIcon =
    TRACK_SIZE_OPTIONS.find((option) => option.id === trackSizePreset)?.icon ?? Rows3

  /**
   * Apply a track-size preset: persist the choice as a local setting and resize
   * every track to the preset height in one undoable step.
   */
  const handleSelectTrackSize = useCallback(
    (preset: (typeof TRACK_SIZE_OPTIONS)[number]) => {
      setTrackSizePreset(preset.id)
      resizeAllTracks(preset.height)
    },
    [setTrackSizePreset],
  )

  const handleDeleteTrack = useCallback(
    (trackId: string) => {
      if (tracks.length <= 1) {
        logger.warn('Cannot remove all tracks')
        return
      }

      removeTracks([trackId])

      const remainingTracks = tracks.filter((track) => track.id !== trackId)
      syncTrackSelectionAfterRemoval([trackId], remainingTracks[0]?.id ?? null)
    },
    [removeTracks, syncTrackSelectionAfterRemoval, tracks],
  )

  const handleDeleteEmptyTracks = useCallback(
    (contextTrackId: string) => {
      const emptyTrackIds = getEmptyTrackIdsForRemoval(
        tracks,
        useItemsStore.getState().itemsByTrackId,
        contextTrackId,
      )
      if (emptyTrackIds.length === 0) return

      const removedTrackIdsSet = new Set(emptyTrackIds)
      removeTracks(emptyTrackIds)

      const remainingTracks = tracks.filter((track) => !removedTrackIdsSet.has(track.id))
      syncTrackSelectionAfterRemoval(emptyTrackIds, remainingTracks[0]?.id ?? null)
    },
    [removeTracks, syncTrackSelectionAfterRemoval, tracks],
  )

  /**
   * Handle removing selected tracks
   * Removes all selected tracks or the active track if none selected.
   * Keeps at least one track in the timeline.
   */
  const handleRemoveTracks = useCallback(() => {
    const tracksToRemove =
      selectedTrackIds.length > 0 ? [...selectedTrackIds] : activeTrackId ? [activeTrackId] : []

    if (tracksToRemove.length === 0) return

    // Don't allow removing all tracks
    const tracksToRemoveSet = new Set(tracksToRemove)
    if (tracksToRemoveSet.size >= tracks.length) {
      logger.warn('Cannot remove all tracks')
      return
    }

    removeTracks(tracksToRemove)

    const remainingTrack = tracks.find((t) => !tracksToRemoveSet.has(t.id))
    syncTrackSelectionAfterRemoval(tracksToRemove, remainingTrack?.id ?? null)
  }, [activeTrackId, removeTracks, selectedTrackIds, syncTrackSelectionAfterRemoval, tracks])

  const unifiedDropIndicatorIndex =
    isTrackDragging && dropIndicatorIndex >= 0 && dropIndicatorIndex <= visibleTracks.length
      ? dropIndicatorIndex
      : -1

  const renderTrackHeaders = () => (
    <div
      ref={trackHeadersScrollRef}
      className="h-full overflow-hidden"
      data-track-section-scroll="all"
    >
      <div className="relative min-h-full">
        {/* Top new-track zone (creates new video track on drop) */}
        <div
          aria-hidden="true"
          data-track-header-new-zone="video"
          style={{ height: `${TOP_ZONE_HEIGHT}px` }}
        />

        {visibleTracks.map((track, index) => (
          <TrackRowFrame
            key={track.id}
            showTopDivider={index === 0}
            onResizeMouseDown={(event) => handleTrackResizeStart(event, track.id)}
            onResizeDoubleClick={(event) => handleTrackResizeReset(event, track.id)}
            resizeHandleLabel={`Resize ${track.name} height`}
            resizeHandlePosition={getTrackKind(track) === 'video' ? 'top' : 'bottom'}
          >
            <TrackHeader
              track={track}
              isActive={activeTrackId === track.id}
              isSelected={selectedTrackIdsSet.has(track.id)}
              canDeleteTrack={tracks.length > 1}
              canDeleteEmptyTracks={canDeleteEmptyTracks}
              onToggleLock={() => toggleTrackLock(track.id)}
              onToggleSyncLock={() => toggleTrackSyncLock(track.id)}
              onToggleDisabled={() => toggleTrackDisabled(track.id)}
              onToggleSolo={() => toggleTrackSolo(track.id)}
              onCloseGaps={() => useTimelineStore.getState().closeAllGapsOnTrack(track.id)}
              onAddVideoTrack={addVideoTrackToTop}
              onAddAudioTrack={appendAudioTrackToSection}
              onDeleteTrack={() => handleDeleteTrack(track.id)}
              onDeleteEmptyTracks={() => handleDeleteEmptyTracks(track.id)}
              onSelect={(e) => {
                if (trackDragJustDroppedRef.current) return
                if (e.shiftKey && activeTrackId) {
                  const startIdx = visibleTracks.findIndex((t) => t.id === activeTrackId)
                  const endIdx = visibleTracks.findIndex((t) => t.id === track.id)
                  if (startIdx !== -1 && endIdx !== -1) {
                    const lo = Math.min(startIdx, endIdx)
                    const hi = Math.max(startIdx, endIdx)
                    const rangeIds = visibleTracks.slice(lo, hi + 1).map((t) => t.id)
                    selectTracks(rangeIds)
                  }
                } else if (e.metaKey || e.ctrlKey) {
                  toggleTrackSelection(track.id)
                } else {
                  setActiveTrack(track.id)
                }
              }}
            />
          </TrackRowFrame>
        ))}

        {/* Bottom new-track zone (creates new audio track on drop) */}
        <div
          aria-hidden="true"
          data-track-header-new-zone="audio"
          style={{ height: `${bottomZoneHeight}px` }}
        />

        {unifiedDropIndicatorIndex >= 0 && (
          <div
            className="absolute left-0 right-0 h-0.5 pointer-events-none z-50 shadow-lg bg-primary"
            style={{
              top: getTrackStackOffset(visibleTracks, unifiedDropIndicatorIndex, TOP_ZONE_HEIGHT),
            }}
          />
        )}
      </div>
    </div>
  )

  return (
    <div
      className="timeline-bg h-full border-t border-border flex flex-col overflow-hidden"
      role="region"
      aria-label="时间线"
    >
      {/* Timeline Header */}
      <TimelineHeader
        onZoomChange={zoomHandlers?.handleZoomChange}
        onZoomIn={zoomHandlers?.handleZoomIn}
        onZoomOut={zoomHandlers?.handleZoomOut}
        onZoomToFit={zoomHandlers?.handleZoomToFit}
        isScopesPanelOpen={colorScopesOpen}
        onToggleScopesPanel={toggleColorScopesOpen}
      />

      {/* Composition Breadcrumbs - shown when inside a sub-composition */}
      <CompositionBreadcrumbs />

      {/* Timeline Content */}
      <div
        className={`flex-1 flex overflow-hidden min-h-0 ${
          isTimelineHydratingCurrentProject ? 'pointer-events-none opacity-70' : ''
        }`}
        aria-busy={isTimelineHydratingCurrentProject}
      >
        {/* Track Headers Sidebar */}
        <div
          className="border-r border-border panel-bg flex-shrink-0 flex flex-col overflow-x-hidden"
          style={{ width: EDITOR_LAYOUT_CSS_VALUES.timelineSidebarWidth }}
        >
          {/* Tracks label with controls */}
          <div
            className="flex items-center justify-between px-3 border-b border-border bg-secondary/20 flex-shrink-0"
            style={{ height: EDITOR_LAYOUT_CSS_VALUES.timelineTracksHeaderHeight }}
          >
            {/* Track size flyout */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  title="轨道大小"
                >
                  <ActiveTrackSizeIcon className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-[10rem]">
                {TRACK_SIZE_OPTIONS.map((option) => {
                  const OptionIcon = option.icon
                  const isActive = trackSizePreset === option.id
                  return (
                    <DropdownMenuItem
                      key={option.id}
                      onSelect={() => handleSelectTrackSize(option)}
                    >
                      <OptionIcon className="w-4 h-4" />
                      <span className="flex-1">{option.label}</span>
                      {isActive ? <Check className="w-4 h-4" /> : null}
                    </DropdownMenuItem>
                  )
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            <div className="flex items-center gap-1">
              {/* Add track flyout */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    title="添加轨道"
                  >
                    <Plus className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[10rem]">
                  <DropdownMenuItem onSelect={() => addVideoTrackToTop()}>
                    <Video className="w-4 h-4" />
                    <span className="flex-1">添加视频轨道</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => appendAudioTrackToSection()}>
                    <AudioLines className="w-4 h-4" />
                    <span className="flex-1">添加音频轨道</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {/* Remove track button */}
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={handleRemoveTracks}
                disabled={tracks.length === 0 || (!activeTrackId && selectedTrackIds.length === 0)}
                title={
                  tracks.length === 0
                    ? '没有可删除的轨道'
                    : !activeTrackId && selectedTrackIds.length === 0
                      ? '请选择要删除的轨道'
                      : selectedTrackIds.length > 0
                        ? `删除 ${selectedTrackIds.length} 条所选轨道`
                        : '删除当前轨道'
                }
              >
                <Minus className="w-3 h-3" />
              </Button>
            </div>
          </div>

          {/* Track labels - synced scroll (no scrollbar) */}
          <div ref={trackHeadersViewportRef} className="flex-1 overflow-hidden relative">
            <div ref={trackHeadersRootRef} className="flex h-full min-h-0 flex-col">
              {renderTrackHeaders()}
            </div>
          </div>
        </div>

        {/* Timeline Canvas */}
        <TimelineContent
          duration={duration}
          tracks={visibleTracks}
          scrollRef={timelineContentRef}
          tracksScrollRef={trackContentScrollRef}
          tracksPaneHeight={tracksViewportHeight}
          onZoomHandlersReady={setZoomHandlers}
          onMetricsChange={setTimelineMetrics}
        />

        {isTimelineHydratingCurrentProject ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/35 backdrop-blur-[1px]">
            <div className="rounded-md border border-border bg-background/90 px-3 py-1.5 text-xs text-muted-foreground shadow-sm">
              正在加载当前工程时间线...
            </div>
          </div>
        ) : null}
      </div>

      <div className="flex flex-shrink-0 overflow-hidden">
        <div
          className="border-r border-border panel-bg flex-shrink-0"
          style={{ width: EDITOR_LAYOUT_CSS_VALUES.timelineSidebarWidth }}
        />
        <div className="flex-1 min-w-0">
          <TimelineNavigator
            actualDuration={timelineMetrics.actualDuration}
            timelineWidth={timelineMetrics.timelineWidth}
            scrollContainerRef={timelineContentRef}
          />
        </div>
      </div>
      <TransitionDragTooltip />
    </div>
  )
})
